import React from "react";

interface FlipCardProps {
  frontContent: React.ReactNode; // Can be text, image, or any JSX
  backContent: React.ReactNode; // Can be text, image, or any JSX
  width?: string; // Optional width e.g., "w-72"
  height?: string; // Optional height e.g., "h-48"
  isFlipped?: boolean; // Controlled flip state
  onFlip?: () => void; // Callback when card is clicked
}

const FlipCard: React.FC<FlipCardProps> = ({
  frontContent,
  backContent,
  width = "w-72",
  height = "h-48",
  isFlipped = false,
  onFlip,
}) => {
  return (
    <div
      className={`relative ${width} ${height} [perspective:1000px]`}
      role={isFlipped ? undefined : "button"}
      tabIndex={isFlipped ? undefined : 0}
      aria-label={isFlipped ? undefined : "Reveal card"}
      onClick={isFlipped ? undefined : onFlip}
      onKeyDown={
        isFlipped
          ? undefined
          : (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onFlip?.();
              }
            }
      }
    >
      <div
        className={`relative w-full h-full transition-transform duration-700 [transform-style:preserve-3d] [will-change:transform] ${
          isFlipped ? "[transform:rotateY(180deg)]" : ""
        }`}
      >
        {/* Front Side */}
        <div
          aria-hidden={isFlipped}
          inert={isFlipped ? true : undefined}
          className={`absolute w-full h-full bg-blue-50 rounded-xl shadow-sm flex items-center justify-center text-gray-800 text-lg font-medium [backface-visibility:hidden] [transform:translateZ(0)] overflow-hidden border-2 border-blue-100 ${
            isFlipped ? "pointer-events-none" : ""
          }`}
        >
          {frontContent}
        </div>

        {/* Back Side */}
        <div
          aria-hidden={!isFlipped}
          inert={isFlipped ? undefined : true}
          className={`absolute w-full h-full bg-green-50 rounded-xl shadow-sm flex items-center justify-center text-gray-800 text-lg font-medium [backface-visibility:hidden] [transform:rotateY(180deg)_translateZ(0)] overflow-hidden border-2 border-green-100 ${
            isFlipped ? "" : "pointer-events-none"
          }`}
        >
          {backContent}
          <button
            type="button"
            onClick={onFlip}
            className="sr-only focus:not-sr-only focus:absolute focus:right-2 focus:top-2 focus:z-10 focus:rounded focus:bg-white focus:px-2 focus:py-1 focus:text-sm focus:text-blue-700 focus:outline focus:outline-2 focus:outline-offset-2"
          >
            Show front of card
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlipCard;
